import type { KeyboardEvent, MouseEventHandler, ReactElement, ReactNode } from 'react';

import { useTheme } from '@emotion/react';

import type { Link } from '../../../atoms/link';

import * as styles from './selectable-list-item.styles';

type Props = {
  children: ReactNode;
  className?: string;
  onSubmit?: MouseEventHandler;
  endAdornment?: ReactElement;
  component?: 'a' | 'label' | 'div' | typeof Link;
  type?: keyof typeof styles.types;
  state?: 'active' | 'disabled' | 'default';
  to?: string;
};

export const SelectableListItem = ({
  children,
  state,
  type = 'default',
  component: Component = 'div',
  className,
  onSubmit,
  endAdornment,
  ...other
}: Props) => {
  const theme = useTheme();

  const handleKeyDown = (e: KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onSubmit) {
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSubmit(e as any);
    }
  };

  return (
    <Component
      className={className}
      css={[
        styles.root(theme),
        styles.types[type],
        state === 'disabled' && styles.disabled(theme),
        state === 'active' && styles.active(theme),
        !!onSubmit && styles.clickable,
      ]}
      data-testid="selectable-list-item"
      role={onSubmit ? 'button' : undefined}
      tabIndex={onSubmit ? 0 : -1}
      to={other.to}
      onClick={onSubmit}
      onKeyDown={onSubmit ? handleKeyDown : undefined}
      {...other}>
      {children}
      {endAdornment}
    </Component>
  );
};
